import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { RequireAuth } from '@/auth/RequireAuth';
import { OwnerOnly } from '@/auth/role';
import { RequireTenantAuth } from '@/auth/RequireTenantAuth';
import { AdminLayout } from '@/components/AdminLayout';
import { PortalLayout } from '@/components/PortalLayout';
import { AuditPage } from '@/pages/AuditPage';
import { ConsoleUsersPage } from '@/pages/ConsoleUsersPage';
import { LoginPage } from '@/pages/LoginPage';
import { NewTenantPage } from '@/pages/NewTenantPage';
import { LicensesPage } from '@/pages/LicensesPage';
import { EditTenantPage } from '@/pages/EditTenantPage';
import { PlansPage } from '@/pages/PlansPage';
import { DeviceDetailPage } from '@/pages/DeviceDetailPage';
import { TenantDetailPage } from '@/pages/TenantDetailPage';
import { TenantsListPage } from '@/pages/TenantsListPage';
import { DashboardPage } from '@/pages/portal/DashboardPage';
import { PortalLoginPage } from '@/pages/portal/PortalLoginPage';
import { SalesReportsPage } from '@/pages/portal/reports/SalesReportsPage';
import { ShiftReportsPage } from '@/pages/portal/reports/ShiftReportsPage';
import { LedgerPage } from '@/pages/portal/reports/LedgerPage';
import { ReceivablesPayablesPage } from '@/pages/portal/reports/ReceivablesPayablesPage';
import { SalesHistoryPage } from '@/pages/portal/reports/SalesHistoryPage';
import { SaleDetailPage } from '@/pages/portal/reports/SaleDetailPage';
import { TaxReportPage } from '@/pages/portal/reports/TaxReportPage';
import { InventoryReportPage } from '@/pages/portal/reports/InventoryReportPage';
import { QuotationsReportPage } from '@/pages/portal/reports/QuotationsReportPage';
import { DayBookPage } from '@/pages/portal/reports/DayBookPage';
import { ProfitLossPage } from '@/pages/portal/reports/ProfitLossPage';
import { PurchasesReportPage } from '@/pages/portal/reports/PurchasesReportPage';
import { ExpensesReportPage } from '@/pages/portal/reports/ExpensesReportPage';
import { ReturnsWalletPage } from '@/pages/portal/reports/ReturnsWalletPage';
import { DailySalesPage } from '@/pages/portal/reports/DailySalesPage';
import { CustomerStatementPage } from '@/pages/portal/reports/CustomerStatementPage';
import { SupplierStatementPage } from '@/pages/portal/reports/SupplierStatementPage';
import { PurchaseItemsPage } from '@/pages/portal/reports/PurchaseItemsPage';
import { StockAlertsPage } from '@/pages/portal/reports/StockAlertsPage';
import { BranchComparisonPage } from '@/pages/portal/reports/BranchComparisonPage';
import { SalesTimingPage } from '@/pages/portal/reports/SalesTimingPage';
import { TopMoversPage } from '@/pages/portal/reports/TopMoversPage';

/**
 * Old `/portal/*` links keep working: the tenant portal simply moved up to the
 * site root, so the subpath and query string carry straight over. There is
 * deliberately NO equivalent for the old `/admin/*` — advertising the console's
 * new home would undo the point of moving it.
 */
function LegacyPortalRedirect() {
  const { pathname, search } = useLocation();
  const moved = pathname.replace(/^\/portal/, '') || '/';
  return <Navigate to={`${moved}${search}`} replace />;
}

/**
 * Two portals, two guards. The tenant reports portal is the site root (`/`) and
 * the super-admin console lives under `/cp` — an unguessable-ish prefix rather
 * than `/admin`, so scanners poking at the obvious path find nothing. The actual
 * boundary is still the JWT audience: `/cp/*` needs `aud: sa` and `/*` needs
 * `aud: tenant`, and neither guard can see the other's session, so a signed-in
 * tenant hitting /cp is bounced to /cp/login and vice-versa.
 */
export function App() {
  return (
    <Routes>
      {/* Tenant reports portal (P2) — the site root */}
      <Route path="/login" element={<PortalLoginPage />} />
      <Route
        path="/"
        element={
          <RequireTenantAuth>
            <PortalLayout />
          </RequireTenantAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="sales" element={<SalesReportsPage />} />
        <Route path="shifts" element={<ShiftReportsPage />} />
        <Route path="ledger" element={<LedgerPage />} />
        {/* Receivables and payables share one page — two point-in-time balance
            tables side by side, each with its own client-side name search. */}
        <Route path="receivables" element={<ReceivablesPayablesPage />} />
        <Route path="payables" element={<ReceivablesPayablesPage />} />
        <Route path="sales-history" element={<SalesHistoryPage />} />
        <Route path="sales-history/:deviceId/:sourceId" element={<SaleDetailPage />} />
        <Route path="tax" element={<TaxReportPage />} />
        <Route path="inventory" element={<InventoryReportPage />} />
        <Route path="quotations" element={<QuotationsReportPage />} />
        <Route path="day-book" element={<DayBookPage />} />
        <Route path="profit-loss" element={<ProfitLossPage />} />
        <Route path="purchases" element={<PurchasesReportPage />} />
        <Route path="expenses" element={<ExpensesReportPage />} />
        <Route path="returns" element={<ReturnsWalletPage />} />
        <Route path="daily-sales" element={<DailySalesPage />} />
        <Route path="customer-statement" element={<CustomerStatementPage />} />
        <Route path="supplier-statement" element={<SupplierStatementPage />} />
        <Route path="purchase-items" element={<PurchaseItemsPage />} />
        <Route path="stock-alerts" element={<StockAlertsPage />} />
        <Route path="branch-comparison" element={<BranchComparisonPage />} />
        <Route path="sales-timing" element={<SalesTimingPage />} />
        <Route path="top-movers" element={<TopMoversPage />} />
      </Route>

      {/* Super-admin console (P0) */}
      <Route path="/cp/login" element={<LoginPage />} />
      <Route
        path="/cp"
        element={
          <RequireAuth>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route index element={<TenantsListPage />} />
        <Route path="tenants/new" element={<NewTenantPage />} />
        <Route path="tenants/:id" element={<TenantDetailPage />} />
        {/* OwnerOnly: an ISSUER login (create tenants / tills / licences
            only) is sent to Tenants. The API refuses it on these anyway. */}
        <Route path="tenants/:id/edit" element={<OwnerOnly><EditTenantPage /></OwnerOnly>} />
        <Route
          path="tenants/:id/devices/:deviceId"
          element={<DeviceDetailPage />}
        />
        {/* SA operations (P3) */}
        <Route path="licenses" element={<LicensesPage />} />
        <Route path="plans" element={<OwnerOnly><PlansPage /></OwnerOnly>} />
        <Route path="audit" element={<OwnerOnly><AuditPage /></OwnerOnly>} />
        <Route path="console-users" element={<OwnerOnly><ConsoleUsersPage /></OwnerOnly>} />
      </Route>

      <Route path="/portal/*" element={<LegacyPortalRedirect />} />

      {/* Anything else — including the retired /admin/* — lands on the tenant
          portal, which is the public face of the app. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
